import { datadogLogs, StatusType } from '@datadog/browser-logs';
import type { GraphQLFormattedError } from 'graphql';

import { getSessionId, getUrlCorrelationId } from '../../../utils';
import { GraphQLErrorCode } from '../../graphql-client/types';

import { beforeSend } from './beforeSend';
import { scrubSensitiveData } from './utils';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface InputProps extends Record<string, any> {}

const {
  DATADOG_BROWSER_LOG_ENABLED: isBrowserLogEnabled,
  ENV: env,
  VERSION: version,
  SITE: site,
  BRAND: brand,
  LOCALE: country,
} = (typeof window !== 'undefined' && window.CONFIG) || {};

export const initBrowserLog = (globalTags: string[]): void => {
  if (!isBrowserLogEnabled) {
    return;
  }
  // for more info about the configuration:
  // https://docs.datadoghq.com/logs/log_collection/javascript/#configuration
  datadogLogs.init({
    service: 'ca-jobapply-frontend',
    clientToken: 'pubffb7fd563d62982ee566fa94ab6d2792',
    site: 'datadoghq.com',
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    env,
    version,
    beforeSend,
  });

  datadogLogs.setGlobalContext({
    brand,
    country,
    site,
    eeeoh_log_tier: 'tin-plus',
    ...globalTags.reduce((acc, tag) => {
      const [key, value] = tag.split(':');
      return {
        ...acc,
        [key]: value,
      };
    }, {}),
  });
};

const getExtrasDetails = (extras: any, error?: BrowserLogError) => ({
  'seek-sessionID': getSessionId(),
  applicationCorrelationId: getUrlCorrelationId(),
  ...scrubSensitiveData(error as InputProps),
  ...scrubSensitiveData(extras),
});

// We don't want to log these as errors as it creates a huge amount of noise
const shouldIgnoreClientError = (error: BrowserLogError): boolean => {
  const graphErrors = error?.graphQLErrors || [];
  return (
    graphErrors.length > 0 &&
    graphErrors.every(
      (err) =>
        err?.extensions?.code === GraphQLErrorCode.badUserInput ||
        err?.extensions?.code === GraphQLErrorCode.unauthenticated,
    )
  );
};

const log = (
  level: StatusType,
  error: BrowserLogError,
  extras: any = {},
): void => {
  if (!isBrowserLogEnabled) {
    return;
  }

  const shouldIgnore = shouldIgnoreClientError(error);
  if (shouldIgnore) {
    return;
  }

  const extrasDetail = getExtrasDetails(extras, error);

  switch (level) {
    case 'error': {
      datadogLogs.logger.error(
        error.message,
        {
          'error.kind': error.type,
          'error.stack': Boolean(error.stack) && error.stack,
          ...extrasDetail,
        },
        {
          ...error,
          name: error.type,
          stack: error.stack,
          message: error.message,
        },
      );
      break;
    }
    case 'warn': {
      datadogLogs.logger.warn(error.message, {
        'error.kind': error.type,
        'error.stack': Boolean(error.stack) && error.stack,
        ...extrasDetail,
      });
      break;
    }
    case 'info': {
      datadogLogs.logger.info(error.message, {
        ...extrasDetail,
      });
      break;
    }
    case 'debug': {
      datadogLogs.logger.debug(error.message, {
        'error.kind': error.type,
        'error.stack': Boolean(error.stack) && error.stack,
        ...extrasDetail,
      });
      break;
    }
    default: {
      break;
    }
  }
};

export interface BrowserLogError {
  type: string;
  message: string;
  stack?: string;
  graphQLErrors?: readonly GraphQLFormattedError[];
}

export const logError = (error: BrowserLogError, extras: any = {}): void => {
  log(StatusType.error, error, extras);
};

export const logWarn = (error: BrowserLogError, extras: any = {}): void => {
  log(StatusType.warn, error, extras);
};

export const logDebug = (
  message: string,
  type: string,
  extras: any = {},
): void => {
  log(
    StatusType.debug,
    {
      message,
      type,
    },
    extras,
  );
};

export const logInfo = (
  message: string,
  type: string,
  extras: any = {},
): void => {
  log(
    StatusType.info,
    {
      message,
      type,
    },
    extras,
  );
};
